import { useReducer } from 'react';

import type { ValidationState } from './types';

const STORAGE_KEY_PREFIX = 'houzy:partner-cockpit:validation-banner-dismissed';

// Keyed on the ticket, not just the state: a partner can go through several validation tickets over time (e.g.
// after cancelling and resubmitting), and dismissing a banner for an earlier ticket must not suppress the same
// state's banner on a later, unrelated one.
function storageKey(userUuid: string, ticketUuid: string, state: ValidationState): string {
    return `${STORAGE_KEY_PREFIX}:${userUuid}:${ticketUuid}:${state}`;
}

export function isValidationBannerDismissed(userUuid: string, ticketUuid: string, state: ValidationState): boolean {
    try {
        return localStorage.getItem(storageKey(userUuid, ticketUuid, state)) === 'true';
    } catch {
        return false;
    }
}

export function dismissValidationBanner(userUuid: string, ticketUuid: string, state: ValidationState): void {
    try {
        localStorage.setItem(storageKey(userUuid, ticketUuid, state), 'true');
    } catch {
        // Storage may be unavailable (private browsing, full quota) — dismissal just won't persist.
    }
}

// Reads localStorage directly on every render rather than mirroring it into useState, so a change of `userUuid`,
// `ticketUuid`, or `state` is reflected immediately without an effect to keep the two in sync.
export function useValidationBannerDismissed(
    userUuid: string | undefined,
    ticketUuid: string | undefined,
    state: ValidationState | undefined,
): { dismissed: boolean; dismiss: () => void } {
    const [, forceRender] = useReducer((count: number) => count + 1, 0);

    const canDismiss = userUuid !== undefined && ticketUuid !== undefined && state !== undefined;

    return {
        dismissed: canDismiss && isValidationBannerDismissed(userUuid, ticketUuid, state),
        dismiss: (): void => {
            if (canDismiss) {
                dismissValidationBanner(userUuid, ticketUuid, state);
                forceRender();
            }
        },
    };
}
